const CART_ID_BLOCK_SELECTOR = '.cartlytics-cart-id-block';
const CART_ID_TOKEN_SELECTOR = '#cartlytics-cart-id, .cartlytics-cart-id-block__token';
const CART_ID_BOX_SELECTOR = '.cartlytics-cart-id-block__box';
const CART_ID_COPY_SELECTOR = '#cartlytics-cart-id-copy, .cartlytics-cart-id-block__copy';
const CART_ID_SHORT_LENGTH = 5;
const NATIVE_CART_ID_BLOCK_SELECTOR = '[data-sunco-native-cart-id]';
const CART_CHAT_TRIGGER_SELECTOR = '[data-sunco-cart-chat]';
const CART_CHAT_MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const LIVE_CHAT_EVENT = 'sunco:open-live-chat';

if (window.suncoCartIdShortenerInitialized !== true) {
  window.suncoCartIdShortenerInitialized = true;
  let cartTokenPromise = null;

  const normalizeCartId = (cartId = '') => String(cartId ?? '').trim().split('?')[0].trim();

  const getCartToken = () => {
    if (!cartTokenPromise) {
      cartTokenPromise = fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => normalizeCartId(cart?.token || cart?.id || ''))
        .catch(() => '')
        .then((cartId) => {
          if (!cartId) {
            cartTokenPromise = null;
          }

          return cartId;
        });
    }

    return cartTokenPromise;
  };

  const getShortCartId = (cartId) => {
    const normalizedCartId = normalizeCartId(cartId);

    if (normalizedCartId.length <= CART_ID_SHORT_LENGTH) {
      return normalizedCartId;
    }

    return normalizedCartId.slice(-CART_ID_SHORT_LENGTH);
  };

  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_error) {
      const textarea = document.createElement('textarea');

      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.insetBlockStart = '0';
      textarea.style.insetInlineStart = '-9999px';
      document.body.append(textarea);
      textarea.select();

      try {
        return document.execCommand('copy');
      } catch (_fallbackError) {
        return false;
      } finally {
        textarea.remove();
      }
    }
  };

  const bindLiveChat = (block) => {
    const chatTrigger = block.querySelector(CART_CHAT_TRIGGER_SELECTOR);

    if (!chatTrigger || chatTrigger.dataset.suncoCartChatBound === 'true') return;

    chatTrigger.dataset.suncoCartChatBound = 'true';
    chatTrigger.addEventListener('click', async (event) => {
      if (document.documentElement.dataset.suncoLiveChatEventReady !== 'true') return;

      event.preventDefault();

      const drawerDialog = window.matchMedia(CART_CHAT_MOBILE_MEDIA_QUERY).matches
        ? chatTrigger.closest('.cart-drawer__dialog[open]')
        : null;

      if (!drawerDialog) {
        window.dispatchEvent(new CustomEvent(LIVE_CHAT_EVENT));
        return;
      }

      const drawerComponent = drawerDialog.closest('cart-drawer-component');

      if (drawerComponent && typeof drawerComponent.closeDialog === 'function') {
        await drawerComponent.closeDialog();
      }

      window.requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent(LIVE_CHAT_EVENT));
      });
    });
  };

  const shortenCartIdBlock = (block) => {
    const tokenElement = block.querySelector(CART_ID_TOKEN_SELECTOR);
    const boxElement = block.querySelector(CART_ID_BOX_SELECTOR);
    const copyButton = block.querySelector(CART_ID_COPY_SELECTOR);

    bindLiveChat(block);

    if (!tokenElement) return;

    const currentDisplayValue = tokenElement.textContent?.trim() || '';
    const existingFullCartId = tokenElement.dataset.suncoFullCartId || '';
    const existingShortCartId = tokenElement.dataset.suncoShortCartId || '';
    const rawFullCartId =
      currentDisplayValue.length > CART_ID_SHORT_LENGTH && currentDisplayValue !== existingShortCartId
        ? currentDisplayValue
        : existingFullCartId || currentDisplayValue;
    const fullCartId = normalizeCartId(rawFullCartId);
    const shortCartId = getShortCartId(fullCartId);

    if (!fullCartId || !shortCartId) {
      if (block.matches(NATIVE_CART_ID_BLOCK_SELECTOR) && block.dataset.suncoCartIdHydrating !== 'true') {
        block.dataset.suncoCartIdHydrating = 'true';

        getCartToken().then((cartId) => {
          delete block.dataset.suncoCartIdHydrating;

          if (!cartId) return;

          tokenElement.textContent = cartId;
          tokenElement.dataset.suncoFullCartId = cartId;
          tokenElement.dataset.suncoShortCartId = getShortCartId(cartId);
          shortenCartIdBlock(block);
        });
      }

      return;
    }

    if (existingFullCartId !== fullCartId) {
      tokenElement.dataset.suncoFullCartId = fullCartId;
    }

    if (existingShortCartId !== shortCartId) {
      tokenElement.dataset.suncoShortCartId = shortCartId;
    }

    if (currentDisplayValue !== shortCartId) {
      tokenElement.textContent = shortCartId;
    }

    const ariaLabel = `Cart ID ending in ${shortCartId}`;
    if (tokenElement.getAttribute('aria-label') !== ariaLabel) {
      tokenElement.setAttribute('aria-label', ariaLabel);
    }

    if (boxElement?.getAttribute('aria-label') !== ariaLabel) {
      boxElement?.setAttribute('aria-label', ariaLabel);
    }

    block.removeAttribute('data-sunco-cart-id-pending');

    if (copyButton?.disabled === true) {
      copyButton.disabled = false;
    }

    if (!copyButton || copyButton.dataset.suncoFullCopyBound === 'true') return;

    copyButton.dataset.suncoFullCopyBound = 'true';
    copyButton.addEventListener(
      'click',
      async (event) => {
        const latestFullCartId = normalizeCartId(tokenElement.dataset.suncoFullCartId || fullCartId);

        if (!latestFullCartId) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        try {
          const copied = await copyTextToClipboard(latestFullCartId);

          if (!copied) return;

          copyButton.setAttribute('aria-label', 'Copied complete cart ID');
          const label = copyButton.querySelector('.cartlytics-cart-id-block__copy-label');

          if (label) {
            label.textContent = 'Copied';
            window.setTimeout(() => {
              label.textContent = 'Copy';
              copyButton.setAttribute('aria-label', 'Copy complete cart ID');
            }, 1600);
          }
        } catch (_error) {}
      },
      { capture: true }
    );
  };

  const shortenCartIds = () => {
    const cartIdBlocks = Array.from(document.querySelectorAll(CART_ID_BLOCK_SELECTOR));
    const hasNativeCartIdBlock = cartIdBlocks.some((block) => block.matches(NATIVE_CART_ID_BLOCK_SELECTOR));

    cartIdBlocks.forEach((block) => {
      if (hasNativeCartIdBlock && !block.matches(NATIVE_CART_ID_BLOCK_SELECTOR)) {
        block.hidden = true;
        block.setAttribute('aria-hidden', 'true');
        block.dataset.suncoCartIdDuplicateHidden = 'true';
        return;
      }

      shortenCartIdBlock(block);
    });
  };

  const scheduleCartIdRetries = () => {
    [0, 250, 750, 1500, 3000, 6000, 10000].forEach((delay) => {
      window.setTimeout(shortenCartIds, delay);
    });
  };

  scheduleCartIdRetries();

  document.addEventListener('DOMContentLoaded', scheduleCartIdRetries);
  document.addEventListener('shopify:section:load', scheduleCartIdRetries);
  document.addEventListener('cart:update', scheduleCartIdRetries);
  document.addEventListener('cart:updated', scheduleCartIdRetries);
  document.addEventListener('cart:refresh', scheduleCartIdRetries);
  document.addEventListener('cart-drawer:open', scheduleCartIdRetries);
}
